import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import apiService from './apiService';

// Firebase configuration
// This should be loaded from environment variables in production
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let app = null;
let messaging = null;
let currentToken = null;

/**
 * Initialize Firebase and messaging
 */
const initializeFirebase = () => {
  try {
    if (!app) {
      app = initializeApp(firebaseConfig);
      messaging = getMessaging(app);
    }
    return messaging;
  } catch (error) {
    console.error('Error initializing Firebase:', error);
    return null;
  }
};

/**
 * Request notification permission from the browser
 */
export const requestNotificationPermission = async () => {
  try {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

/**
 * Get FCM token for the current browser
 * @param {string} vapidKey - VAPID key for web push
 */
export const getFCMToken = async (vapidKey) => {
  try {
    const messagingInstance = initializeFirebase();
    if (!messagingInstance) {
      return null;
    }

    const hasPermission = await requestNotificationPermission();
    if (!hasPermission) {
      console.warn('Notification permission not granted');
      return null;
    }

    currentToken = await getToken(messagingInstance, { vapidKey });
    console.log('FCM Token obtained:', currentToken);

    // Register token with backend
    await registerTokenWithBackend(currentToken);

    return currentToken;
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return null;
  }
};

/**
 * Register FCM token with backend
 * @param {string} token - FCM token
 */
const registerTokenWithBackend = async (token) => {
  try {
    const deviceInfo = {
      browser: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
    };

    const response = await apiService.post('/api/notifications/register-token', {
      token,
      platform: 'web',
      deviceInfo,
    });

    if (response.success) {
      console.log('Token registered with backend successfully');
      return true;
    } else {
      console.warn('Failed to register token with backend');
      return false;
    }
  } catch (error) {
    console.error('Error registering token with backend:', error);
    return false;
  }
};

/**
 * Unregister FCM token from backend
 * @param {string} token - FCM token (optional, uses current token if not provided)
 */
export const unregisterToken = async (token = null) => {
  try {
    const tokenToUnregister = token || currentToken;
    if (!tokenToUnregister) {
      console.warn('No token to unregister');
      return false;
    }

    const response = await apiService.post('/api/notifications/unregister-token', {
      token: tokenToUnregister,
    });

    if (response.success) {
      console.log('Token unregistered successfully');
      currentToken = null;
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error unregistering token:', error);
    return false;
  }
};

/**
 * Set up foreground message handler
 * @param {Function} callback - Function to call when a message is received
 */
export const onForegroundMessage = (callback) => {
  const messagingInstance = initializeFirebase();
  if (!messagingInstance) {
    return null;
  }

  return onMessage(messagingInstance, (payload) => {
    console.log('Foreground message received:', payload);

    // Show browser notification if permission granted
    if (Notification.permission === 'granted') {
      const { title, body, icon, image } = payload.notification || {};
      const notification = new Notification(title || 'Hebrew Hearts', {
        body: body || 'You have a new notification',
        icon: icon || '/logo.png',
        image: image,
        badge: '/badge.png',
        data: payload.data,
        tag: payload.data?.type || 'default',
      });

      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();

        // Handle notification click based on type
        if (payload.data?.screen) {
          navigateToScreen(payload.data);
        }

        notification.close();
      };
    }

    // Call custom callback
    if (callback && typeof callback === 'function') {
      callback(payload);
    }
  });
};

/**
 * Navigate to appropriate screen based on notification data
 * @param {Object} data - Notification data
 */
const navigateToScreen = (data) => {
  const { screen, conversationId, matchId, userId } = data;

  switch (screen) {
    case 'Chat':
      if (conversationId) {
        window.location.href = `/chat/${conversationId}`;
      }
      break;
    case 'MatchReveal':
      if (matchId) {
        window.location.href = `/matches/${matchId}`;
      }
      break;
    case 'Profile':
      if (userId) {
        window.location.href = `/profile/${userId}`;
      }
      break;
    case 'Home':
    default:
      window.location.href = '/';
      break;
  }
};

/**
 * Get badge counts from backend
 */
export const getBadgeCounts = async () => {
  try {
    const response = await apiService.get('/api/notifications/badge-counts');
    return response.badgeCounts;
  } catch (error) {
    console.error('Error getting badge counts:', error);
    return null;
  }
};

/**
 * Reset badge count
 * @param {string} type - Optional type to reset specific badge (newMatches, unreadMessages, etc.)
 */
export const resetBadgeCount = async (type = null) => {
  try {
    await apiService.post('/api/notifications/badge-counts/reset', {
      type,
    });
    return true;
  } catch (error) {
    console.error('Error resetting badge count:', error);
    return false;
  }
};

/**
 * Update notification preferences
 * @param {Object} preferences - Notification preferences
 */
export const updateNotificationPreferences = async (preferences) => {
  try {
    if (!currentToken) {
      console.warn('No token available to update preferences');
      return false;
    }

    const response = await apiService.put(`/api/notifications/tokens/${currentToken}/preferences`, {
      preferences,
    });

    return response.success;
  } catch (error) {
    console.error('Error updating notification preferences:', error);
    return false;
  }
};

/**
 * Subscribe to a notification topic
 * @param {string} topic - Topic name
 */
export const subscribeToTopic = async (topic) => {
  try {
    if (!currentToken) {
      console.warn('No token available to subscribe to topic');
      return false;
    }

    const response = await apiService.post('/api/notifications/topics/subscribe', {
      token: currentToken,
      topic,
    });

    return response.success;
  } catch (error) {
    console.error('Error subscribing to topic:', error);
    return false;
  }
};

/**
 * Unsubscribe from a notification topic
 * @param {string} topic - Topic name
 */
export const unsubscribeFromTopic = async (topic) => {
  try {
    if (!currentToken) {
      console.warn('No token available to unsubscribe from topic');
      return false;
    }

    const response = await apiService.post('/api/notifications/topics/unsubscribe', {
      token: currentToken,
      topic,
    });

    return response.success;
  } catch (error) {
    console.error('Error unsubscribing from topic:', error);
    return false;
  }
};

/**
 * Send a test notification
 */
export const sendTestNotification = async () => {
  try {
    const response = await apiService.post('/api/notifications/test');
    return response.success;
  } catch (error) {
    console.error('Error sending test notification:', error);
    return false;
  }
};

/**
 * Check if notifications are supported
 */
export const isNotificationSupported = () => {
  return 'Notification' in window && 'serviceWorker' in navigator;
};

/**
 * Get current notification permission status
 */
export const getNotificationPermission = () => {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
};

// Legacy function for backward compatibility
export const fetchNotifications = () => {
  // Deprecated - use getBadgeCounts instead
  console.warn('fetchNotifications is deprecated, use getBadgeCounts instead');
  return [];
};
